import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import PaperDraggable from "../../../../../components/PaperDraggable";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState } from "react";

export default function DialogIncluirEditarIngredienteAtivo({
                                                                open,
                                                                handleClose,
                                                                defaultValue
                                                            }: DialogIncluirIngredienteAtivoProps) {
    const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const queryClient = useQueryClient();

    const { mutate: criarIngrediente, isLoading: isLoadingCriar } = useMutation({
        mutationKey: "cadastrar-ingrediente-ativo",
        mutationFn: (inputs: Omit<IIngredienteAtivo, "id">) =>
            axiosClient.post("/receituario-agronomico/ingredientes-ativos/adicionar-ingrediente-ativo", inputs),
        onError: () => {
            enqueueSnackbar("Erro ao cadastrar ingrediente ativo!", { variant: "error" });
            setIsSubmitting(false);
        },
        onSuccess: () => {
            enqueueSnackbar("Ingrediente ativo cadastrado com sucesso!", { variant: "success" });
            queryClient.invalidateQueries("pesquisar-ingredientes-ativos");
            setIsSubmitting(false);
            handleClose();
        },
    });

    const { mutate: editarIngrediente, isLoading: isLoadingEditar } = useMutation({
        mutationKey: "editar-ingrediente-ativo",
        mutationFn: (inputs: IIngredienteAtivo) =>
            axiosClient.put(`/receituario-agronomico/ingredientes-ativos/editar-ingrediente-ativo/${inputs.id}`, {
                nome: inputs.nome,
            }),
        onError: () => {
            enqueueSnackbar("Erro ao editar ingrediente ativo!", { variant: "error" });
            setIsSubmitting(false);
        },
        onSuccess: () => {
            enqueueSnackbar("Ingrediente ativo editado com sucesso!", { variant: "success" });
            queryClient.invalidateQueries("pesquisar-ingredientes-ativos");
            setIsSubmitting(false);
            handleClose();
        },
    });

    const valoresIniciais = useMemo(() => defaultValue ?? null, [defaultValue]);
    const methods = useForm<IIngredienteAtivo>({
        defaultValues: {
            id: valoresIniciais?.id ?? undefined,
            nome: valoresIniciais?.nome ?? "",
        }
    });
    const { handleSubmit, reset } = methods;

    const onSubmit = (data: IIngredienteAtivo) => {
        setIsSubmitting(true);
        if (isEdit && data.id) {
            editarIngrediente(data);
        } else {
            criarIngrediente({ nome: data.nome });
        }
        reset();
    };

    const handleCloseDialog = () => {
        reset();
        handleClose();
    };

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={handleCloseDialog}
                maxWidth="xs"
                fullWidth
                PaperComponent={PaperDraggable}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle id="draggable-dialog-title" style={{ cursor: 'move' }}>
                    {isEdit ? "Editar Ingrediente Ativo" : "Cadastrar Ingrediente Ativo"}
                </DialogTitle>
                <DialogContent>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <TextField
                            label="Nome"
                            fullWidth
                            autoFocus
                            {...methods.register("nome", { required: "O nome do ingrediente é obrigatório" })}
                            disabled={isSubmitting}
                            margin="normal"
                        />
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="text"
                        onClick={handleCloseDialog}
                        disabled={isSubmitting}
                    >
                        Fechar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isLoadingEditar || isLoadingCriar}
                    >
                        {(isLoadingEditar || isLoadingCriar) && (
                            <CircularProgress size={20} sx={{ marginRight: 1 }} />
                        )}
                        {isEdit ? "Atualizar" : "Cadastrar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </FormProvider>
    );
}

// types para os props
export interface DialogIncluirIngredienteAtivoProps {
    open: boolean;
    handleClose: () => void;
    defaultValue?: IIngredienteAtivo | null;
}

export interface IIngredienteAtivo {
    id: number;
    nome: string;
}